import { UploadFile } from 'antd/es/upload/interface';

export interface FileOperations {
  getCurrentQuestionFiles: (questionFiles: Record<number, UploadFile[]>, currentQuestion: number) => File[];
  parseQuestionNumber: (displayName: string) => number | null;
  getUploadedFilesForCurrentQuestion: (listOfFiles: any, currentQuestion: number) => any[];
  getAllFilesForCurrentQuestion: (
    listOfFiles: any,
    currentFileList: UploadFile[],
    currentQuestion: number
  ) => any[];
  getNewFilesToUpload: (
    listOfFiles: any,
    questionFiles: Record<number, UploadFile[]>,
    currentQuestion: number
  ) => File[];
}

export const fileOperations: FileOperations = {
  getCurrentQuestionFiles: (questionFiles: Record<number, UploadFile[]>, currentQuestion: number): File[] => {
    const filesForCurrent = questionFiles[currentQuestion] || [];
    return filesForCurrent
      .map((file) => file.originFileObj)
      .filter(Boolean) as File[];
  },

  parseQuestionNumber: (displayName: string): number | null => {
    const match = displayName.match(/^Q(\d+)_/);
    return match ? parseInt(match[1], 10) - 1 : null;
  },

  getUploadedFilesForCurrentQuestion: (listOfFiles: any, currentQuestion: number) => {
    if (!listOfFiles?.files) return [];
    
    return listOfFiles.files.filter((file: any) => {
      const questionNumber = fileOperations.parseQuestionNumber(file.display_name);
      return questionNumber === currentQuestion;
    });
  },

  getAllFilesForCurrentQuestion: (
    listOfFiles: any,
    currentFileList: UploadFile[],
    currentQuestion: number
  ) => {
    const uploadedFiles = fileOperations.getUploadedFilesForCurrentQuestion(listOfFiles, currentQuestion);
    const localFiles = currentFileList.map(file => ({
      file_id: file.uid,
      display_name: file.name,
      file_type: "RFP",
      isLocal: true,
      uploadFile: file
    }));
    
    return [...uploadedFiles.map(file => ({ ...file, isLocal: false })), ...localFiles];
  },

  getNewFilesToUpload: (
    listOfFiles: any,
    questionFiles: Record<number, UploadFile[]>,
    currentQuestion: number
  ): File[] => {
    const uploadedFileNames = fileOperations.getUploadedFilesForCurrentQuestion(listOfFiles, currentQuestion)
      .map((f: any) => f.display_name);
    const localFiles = fileOperations.getCurrentQuestionFiles(questionFiles, currentQuestion);
    
    return localFiles.filter(file => {
      const hasPrefix = /^Q\d+_/.test(file.name);
      if (hasPrefix) {
        return !uploadedFileNames.includes(file.name);
      }
      return true;
    });
  },
};

export const validateFile = (file: File): { isValid: boolean; error?: string } => {
  const allowed = ["pdf", "doc", "docx", "csv", "xls", "xlsx"];
  const ext = file.name.split(".").pop()?.toLowerCase();
  
  if (!ext || !allowed.includes(ext)) {
    return { isValid: false, error: "Invalid file type - Only PDF, DOC, DOCX, CSV, XLS, XLSX allowed." };
  }
  
  if (file.size > 25 * 1024 * 1024) {
    return { isValid: false, error: "File too large - Max size is 25MB." };
  }
  
  return { isValid: true };
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k**i).toFixed(2))  } ${  sizes[i]}`;
};
